(() => {
  if (window.ZJReviewsCarousel) {
    window.ZJReviewsCarousel.initAll();
    return;
  }

  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 989.98px)');
  const PX_PER_SECOND = 40;

  function init(root) {
    if (!root) return;
    if (instances.has(root)) {
      instances.get(root).refresh();
      return;
    }

    const viewport = root.querySelector('.zj-reviews-carousel__viewport');
    const track = root.querySelector('.zj-reviews-carousel__track');
    if (!viewport || !track) return;

    let cards = [];
    let setWidth = 0;
    const dots = root.querySelector('[data-review-dots]');

    const step = () => cards.length ? cards[0].getBoundingClientRect().width + Number.parseFloat(getComputedStyle(track).gap || 0) : 0;

    function ensureClones() {
      track.querySelectorAll('[data-zj-clone]').forEach(el => el.remove());
      const originals = [...track.querySelectorAll('.zj-review-card')];
      if (originals.length < 2) return;
      const sets = root.dataset.autoplay === 'true' && mobileQuery.matches ? 2 : 1;
      for (let set = 0; set < sets; set += 1) originals.forEach(card => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-zj-clone', '');
        clone.querySelectorAll('a, button').forEach(el => el.setAttribute('tabindex', '-1'));
        track.appendChild(clone);
      });
    }

    function measureSetWidth() {
      const originals = track.querySelectorAll('.zj-review-card:not([data-zj-clone])');
      if (!originals.length) { setWidth = 0; return; }
      const first = originals[0];
      const last = originals[originals.length - 1];
      const gap = Number.parseFloat(getComputedStyle(track).gap || 0) || 0;
      setWidth = (last.offsetLeft + last.offsetWidth) - first.offsetLeft + gap;
    }

    function applyAutoplayState() {
      const autoplayOn = root.dataset.autoplay === 'true';
      const canAnimate = autoplayOn && !reducedMotion.matches && setWidth > 0 && cards.length > 1;
      root.classList.toggle('zj-reviews-carousel--autoplay', canAnimate);
      viewport.style.touchAction = canAnimate && !mobileQuery.matches ? 'pan-y' : '';
      toggleScrollAutoplay(canAnimate && mobileQuery.matches);
      if (canAnimate) {
        track.style.setProperty('--zj-set-width', setWidth + 'px');
        track.style.setProperty('--zj-duration', (setWidth / PX_PER_SECOND) + 's');
      }
    }

    // Tablet/mobile autoplay drives the native scroll position so touch swiping keeps its momentum.
    let scrollRaf = 0;
    let scrollPaused = false;
    let resumeTimer = 0;
    let lastTick = 0;
    let position = 0;
    let lastSet = 0;
    const holdScroll = () => { scrollPaused = true; clearTimeout(resumeTimer); };
    const releaseScroll = (delay = 1200) => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { position = viewport.scrollLeft; scrollPaused = false; }, delay);
    };
    const wrapScroll = () => {
      if (!setWidth) return;
      if (viewport.scrollLeft >= setWidth * 2) viewport.scrollLeft -= setWidth;
      else if (viewport.scrollLeft < setWidth) viewport.scrollLeft += setWidth;
      position = viewport.scrollLeft;
      lastSet = position;
    };
    const tick = time => {
      scrollRaf = requestAnimationFrame(tick);
      const delta = lastTick ? (time - lastTick) / 1000 : 0;
      lastTick = time;
      if (scrollPaused || document.hidden || !delta) return;
      if (Math.abs(viewport.scrollLeft - lastSet) >= 2) { position = viewport.scrollLeft; lastSet = position; holdScroll(); releaseScroll(900); return; }
      position += PX_PER_SECOND * delta;
      if (Math.abs(position - viewport.scrollLeft) >= 1) {
        viewport.scrollLeft = position;
        lastSet = viewport.scrollLeft;
        wrapScroll();
      }
    };
    const onUserScroll = () => {
      if (!scrollRaf) return;
      if (Math.abs(viewport.scrollLeft - lastSet) < 2 && !scrollPaused) return;
      holdScroll();
      releaseScroll(900);
    };
    const toggleScrollAutoplay = on => {
      if (on && !scrollRaf) {
        viewport.scrollLeft = Math.max(viewport.scrollLeft, setWidth);
        wrapScroll();
        lastTick = 0;
        scrollRaf = requestAnimationFrame(tick);
      } else if (!on && scrollRaf) {
        cancelAnimationFrame(scrollRaf);
        scrollRaf = 0;
        scrollPaused = false;
      }
    };
    ['touchstart', 'pointerdown', 'wheel'].forEach(name => viewport.addEventListener(name, holdScroll, { passive: true }));
    ['touchend', 'touchcancel', 'pointerup', 'pointercancel'].forEach(name => viewport.addEventListener(name, () => releaseScroll(), { passive: true }));
    viewport.addEventListener('scroll', onUserScroll, { passive: true });
    mobileQuery.addEventListener('change', () => { toggleScrollAutoplay(false); refresh(); });

    const move = direction => viewport.scrollBy({ left: direction * step(), behavior: 'auto' });

    const refresh = () => {
      ensureClones();
      cards = [...track.querySelectorAll('.zj-review-card:not([data-zj-clone])')];
      measureSetWidth();
      if (dots) dots.replaceChildren(...cards.map((_, index) => Object.assign(document.createElement('button'), { type: 'button', ariaLabel: `Review ${index + 1}` })));
      applyAutoplayState();
    };

    const paintDots = () => {
      if (!dots || !cards.length || !setWidth) return;
      const pos = ((viewport.scrollLeft % setWidth) + setWidth) % setWidth;
      const active = Math.min(cards.length - 1, Math.max(0, Math.round(pos / Math.max(step(), 1))));
      [...dots.children].forEach((dot, index) => dot.toggleAttribute('aria-current', index === active));
    };

    const DRAG_THRESHOLD = 5;
    let pressed = false;
    let dragging = false;
    let suppressClick = false;
    let startX = 0;
    let startScroll = 0;
    let activePointer = 0;
    let marqueeAnim = null;
    let marqueeStart = 0;
    let touchResume = 0;
    // Desktop autoplay is a CSS transform marquee: drag shifts the animation clock, so the loop stays seamless.
    const marqueeMode = () => root.classList.contains('zj-reviews-carousel--autoplay') && !mobileQuery.matches;
    const down = event => {
      if (event.button !== 0 || (event.pointerType === 'touch' && !marqueeMode())) return;
      clearTimeout(touchResume);
      pressed = true;
      dragging = false;
      startX = event.clientX;
      startScroll = viewport.scrollLeft;
      activePointer = event.pointerId;
      marqueeAnim = marqueeMode() ? track.getAnimations().find(a => a.animationName === 'zjReviewsMarquee') || null : null;
      marqueeStart = marqueeAnim ? Number(marqueeAnim.currentTime) || 0 : 0;
    };
    const drag = event => {
      if (!pressed) return;
      const delta = event.clientX - startX;
      if (!dragging) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        dragging = true;
        root.classList.add('zj-reviews-carousel--dragging');
        viewport.setPointerCapture(activePointer);
        // Pressing focuses the carousel and the CSS pauses on :focus-within; drop it so autoplay can resume after the drag.
        if (marqueeAnim && root.contains(document.activeElement)) document.activeElement.blur();
      }
      if (marqueeAnim && setWidth) {
        const duration = Number(marqueeAnim.effect.getComputedTiming().duration);
        marqueeAnim.currentTime = (((marqueeStart + delta * (duration / setWidth)) % duration) + duration) % duration;
      } else {
        viewport.scrollLeft = startScroll - delta;
      }
    };
    const up = event => {
      const holdAfterTouch = dragging && marqueeAnim && event && event.pointerType === 'touch';
      if (dragging) {
        suppressClick = true;
        setTimeout(() => { suppressClick = false; }, 0);
      }
      pressed = false;
      dragging = false;
      marqueeAnim = null;
      if (holdAfterTouch) touchResume = setTimeout(() => root.classList.remove('zj-reviews-carousel--dragging'), 1200);
      else root.classList.remove('zj-reviews-carousel--dragging');
    };

    viewport.addEventListener('pointerdown', down);
    viewport.addEventListener('pointermove', drag);
    viewport.addEventListener('pointerup', up);
    viewport.addEventListener('pointercancel', up);
    viewport.addEventListener('selectstart', event => { if (pressed) event.preventDefault(); });
    viewport.addEventListener('click', event => {
      if (suppressClick) { event.preventDefault(); event.stopPropagation(); }
    }, true);
    viewport.addEventListener('scroll', paintDots, { passive: true });
    dots?.addEventListener('click', event => {
      const index = [...dots.children].indexOf(event.target);
      if (index >= 0) viewport.scrollTo({ left: index * step(), behavior: 'auto' });
    });
    viewport.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
    });

    refresh();
    if (!root.classList.contains('zj-reviews-carousel--autoplay') && cards.length) {
      viewport.scrollLeft = cards[0].offsetLeft;
    }
    paintDots();

    instances.set(root, { refresh });
  }

  function initAll(scope = document) {
    scope.querySelectorAll('.zj-reviews-carousel').forEach(init);
  }

  function refreshFromEvent(event) {
    const root = event.target.closest?.('.zj-reviews-carousel');
    if (root) init(root); else initAll();
  }

  window.ZJReviewsCarousel = { initAll };
  initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:reorder', () => initAll());
  document.addEventListener('shopify:block:select', refreshFromEvent);
  document.addEventListener('shopify:block:load', refreshFromEvent);
  document.addEventListener('shopify:block:unload', () => requestAnimationFrame(() => initAll()));
})();
